import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  User,
  Briefcase,
  FileText,
  CreditCard,
  Building2,
  Users,
  PiggyBank,
  TrendingUp,
  Shield,
  AlertCircle,
} from "lucide-react";

type Step = "account-type" | "personal-info" | "financial-profile" | "trading-experience" | "documents" | "funding" | "review";

const steps: { id: Step; label: string; icon: typeof User }[] = [
  { id: "account-type", label: "Account Type", icon: Building2 },
  { id: "personal-info", label: "Personal Info", icon: User },
  { id: "financial-profile", label: "Financial Profile", icon: Briefcase },
  { id: "trading-experience", label: "Trading Experience", icon: TrendingUp },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "funding", label: "Funding", icon: CreditCard },
  { id: "review", label: "Review", icon: Check },
];

const accountTypes = [
  { value: "individual", label: "Individual Account", description: "For single ownership", icon: User },
  { value: "joint_twros", label: "Joint WROS", description: "Joint with right of survivorship", icon: Users },
  { value: "joint_tic", label: "Joint TIC", description: "Joint tenants in common", icon: Users },
  { value: "ira_traditional", label: "Traditional IRA", description: "Tax-deferred retirement", icon: PiggyBank },
  { value: "ira_roth", label: "Roth IRA", description: "Tax-free growth", icon: PiggyBank },
  { value: "ira_sep", label: "SEP IRA", description: "Self-employed retirement", icon: PiggyBank },
  { value: "trust", label: "Trust Account", description: "For trusts", icon: Shield },
  { value: "corporate", label: "Corporate Account", description: "For corporations", icon: Building2 },
  { value: "llc", label: "LLC Account", description: "For LLCs", icon: Building2 },
  { value: "margin", label: "Margin Account", description: "Leverage trading", icon: TrendingUp },
];

const personalInfoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  dateOfBirth: z.string().min(1, "Date of birth is required"),
  ssn: z.string().regex(/^\d{3}-?\d{2}-?\d{4}$/, "Invalid SSN format (XXX-XX-XXXX)"),
  phone: z.string().min(10, "Phone number is required"),
  address: z.string().min(1, "Address is required"),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code"),
  country: z.string().default("US"),
});

const financialProfileSchema = z.object({
  employmentStatus: z.string().min(1, "Employment status is required"),
  employer: z.string().optional(),
  occupation: z.string().optional(),
  annualIncome: z.string().min(1, "Annual income is required"),
  netWorth: z.string().min(1, "Net worth is required"),
  liquidNetWorth: z.string().min(1, "Liquid net worth is required"),
  fundingSource: z.string().min(1, "Funding source is required"),
});

const tradingExperienceSchema = z.object({
  stocksExperience: z.string().min(1, "Required"),
  optionsExperience: z.string().min(1, "Required"),
  cryptoExperience: z.string().min(1, "Required"),
  investmentObjective: z.string().min(1, "Investment objective is required"),
  riskTolerance: z.string().min(1, "Risk tolerance is required"),
  timeHorizon: z.string().min(1, "Time horizon is required"),
  enableOptionsTrading: z.boolean().default(false),
  enableCryptoTrading: z.boolean().default(false),
  enableMarginTrading: z.boolean().default(false),
});

const documentsSchema = z.object({
  governmentIdType: z.string().min(1, "ID type is required"),
  acceptedDisclosures: z.boolean().refine((val) => val === true, "You must accept the disclosures"),
  acceptedCustomerAgreement: z.boolean().refine((val) => val === true, "You must accept the customer agreement"),
  acceptedPrivacyPolicy: z.boolean().refine((val) => val === true, "You must accept the privacy policy"),
});

export default function OpenAccountPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState<Step>("account-type");
  const [applicationId, setApplicationId] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    accountType: "",
    clearingCompany: "etna",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    ssn: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
    employmentStatus: "",
    employer: "",
    occupation: "",
    annualIncome: "",
    netWorth: "",
    liquidNetWorth: "",
    fundingSource: "",
    stocksExperience: "",
    optionsExperience: "",
    cryptoExperience: "",
    investmentObjective: "",
    riskTolerance: "",
    timeHorizon: "",
    enableOptionsTrading: false,
    enableCryptoTrading: false,
    enableMarginTrading: false,
    governmentIdType: "",
    acceptedDisclosures: false,
    acceptedCustomerAgreement: false,
    acceptedPrivacyPolicy: false,
    fundingMethod: "",
    initialDeposit: "",
  });

  const personalForm = useForm({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfBirth: formData.dateOfBirth,
      ssn: formData.ssn,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      country: formData.country,
    },
  });

  const financialForm = useForm({
    resolver: zodResolver(financialProfileSchema),
    defaultValues: {
      employmentStatus: formData.employmentStatus,
      employer: formData.employer,
      occupation: formData.occupation,
      annualIncome: formData.annualIncome,
      netWorth: formData.netWorth,
      liquidNetWorth: formData.liquidNetWorth,
      fundingSource: formData.fundingSource,
    },
  });

  const tradingForm = useForm({
    resolver: zodResolver(tradingExperienceSchema),
    defaultValues: {
      stocksExperience: formData.stocksExperience,
      optionsExperience: formData.optionsExperience,
      cryptoExperience: formData.cryptoExperience,
      investmentObjective: formData.investmentObjective,
      riskTolerance: formData.riskTolerance,
      timeHorizon: formData.timeHorizon,
      enableOptionsTrading: formData.enableOptionsTrading,
      enableCryptoTrading: formData.enableCryptoTrading,
      enableMarginTrading: formData.enableMarginTrading,
    },
  });

  const documentsForm = useForm({
    resolver: zodResolver(documentsSchema),
    defaultValues: {
      governmentIdType: formData.governmentIdType,
      acceptedDisclosures: formData.acceptedDisclosures,
      acceptedCustomerAgreement: formData.acceptedCustomerAgreement,
      acceptedPrivacyPolicy: formData.acceptedPrivacyPolicy,
    },
  });

  const createApplicationMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/applications", data);
      return res.json();
    },
    onSuccess: (data) => {
      setApplicationId(data.id);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to create application", variant: "destructive" });
    },
  });

  const updateApplicationMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("PATCH", `/api/applications/${applicationId}`, data);
      return res.json();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save progress", variant: "destructive" });
    },
  });

  const submitApplicationMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/applications/${applicationId}/submit`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Success", description: "Your application has been submitted for review" });
      queryClient.invalidateQueries({ queryKey: ["/api/applications"] });
      navigate("/dashboard");
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to submit application", variant: "destructive" });
    },
  });

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const goToNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id);
    }
  };

  const goToPrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id);
    }
  };

  const handleAccountTypeSelect = async (accountType: string) => {
    setFormData({ ...formData, accountType });
    if (!applicationId) {
      await createApplicationMutation.mutateAsync({ accountType });
    } else {
      await updateApplicationMutation.mutateAsync({ accountType });
    }
    goToNextStep();
  };

  const handlePersonalInfoSubmit = async (data: z.infer<typeof personalInfoSchema>) => {
    setFormData({ ...formData, ...data });
    if (applicationId) {
      await updateApplicationMutation.mutateAsync(data);
    }
    goToNextStep();
  };

  const handleFinancialProfileSubmit = async (data: z.infer<typeof financialProfileSchema>) => {
    setFormData({ ...formData, ...data });
    if (applicationId) {
      await updateApplicationMutation.mutateAsync(data);
    }
    goToNextStep();
  };

  const handleTradingExperienceSubmit = async (data: z.infer<typeof tradingExperienceSchema>) => {
    setFormData({ ...formData, ...data });
    if (applicationId) {
      await updateApplicationMutation.mutateAsync({
        ...data,
        tradingPermissions: {
          stocks: true,
          options: data.enableOptionsTrading,
          crypto: data.enableCryptoTrading,
          margin: data.enableMarginTrading,
        },
      });
    }
    goToNextStep();
  };

  const handleDocumentsSubmit = async (data: z.infer<typeof documentsSchema>) => {
    setFormData({ ...formData, ...data });
    if (applicationId) {
      await updateApplicationMutation.mutateAsync(data);
    }
    goToNextStep();
  };

  const handleFundingSubmit = async () => {
    goToNextStep();
  };

  const handleFinalSubmit = async () => {
    await submitApplicationMutation.mutateAsync();
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="button-back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Open New Account</h1>
              <p className="text-sm text-muted-foreground">Complete the steps below to open your trading account</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = index < currentStepIndex;
              return (
                <div key={step.id} className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                      isCompleted
                        ? "bg-primary text-primary-foreground"
                        : isActive
                        ? "bg-primary/20 text-primary border-2 border-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isCompleted ? <Check className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
                  </div>
                  <span className={`text-xs ${isActive ? "text-primary font-medium" : "text-muted-foreground"} hidden md:block`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {currentStep === "account-type" && (
          <Card>
            <CardHeader>
              <CardTitle>Select Account Type</CardTitle>
              <CardDescription>Choose the type of account that best fits your needs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {accountTypes.map((type) => {
                  const TypeIcon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => handleAccountTypeSelect(type.value)}
                      className={`p-4 border rounded-lg text-left transition-colors hover-elevate ${
                        formData.accountType === type.value ? "border-primary bg-primary/10" : "border-border"
                      }`}
                      data-testid={`button-account-type-${type.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          <TypeIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{type.label}</p>
                          <p className="text-sm text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === "personal-info" && (
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Please provide your personal details</CardDescription>
            </CardHeader>
            <Form {...personalForm}>
              <form onSubmit={personalForm.handleSubmit(handlePersonalInfoSubmit)}>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={personalForm.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John" {...field} data-testid="input-first-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalForm.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Doe" {...field} data-testid="input-last-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={personalForm.control}
                      name="dateOfBirth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} data-testid="input-dob" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalForm.control}
                      name="ssn"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Social Security Number</FormLabel>
                          <FormControl>
                            <Input placeholder="XXX-XX-XXXX" {...field} data-testid="input-ssn" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={personalForm.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="(555) 123-4567" {...field} data-testid="input-phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={personalForm.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Address</FormLabel>
                        <FormControl>
                          <Input placeholder="123 Main St" {...field} data-testid="input-address" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FormField
                      control={personalForm.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem className="col-span-2">
                          <FormLabel>City</FormLabel>
                          <FormControl>
                            <Input placeholder="City" {...field} data-testid="input-city" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalForm.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <FormControl>
                            <Input placeholder="CA" {...field} data-testid="input-state" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={personalForm.control}
                      name="zipCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ZIP Code</FormLabel>
                          <FormControl>
                            <Input placeholder="12345" {...field} data-testid="input-zip" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between gap-2">
                  <Button type="button" variant="outline" onClick={goToPrevStep} data-testid="button-prev">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button type="submit" data-testid="button-next">
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        )}

        {currentStep === "financial-profile" && (
          <Card>
            <CardHeader>
              <CardTitle>Financial Profile</CardTitle>
              <CardDescription>Help us understand your financial situation</CardDescription>
            </CardHeader>
            <Form {...financialForm}>
              <form onSubmit={financialForm.handleSubmit(handleFinancialProfileSubmit)}>
                <CardContent className="space-y-4">
                  <FormField
                    control={financialForm.control}
                    name="employmentStatus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employment Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-employment">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="employed">Employed</SelectItem>
                            <SelectItem value="self_employed">Self-Employed</SelectItem>
                            <SelectItem value="retired">Retired</SelectItem>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="unemployed">Unemployed</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={financialForm.control}
                      name="employer"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Employer Name (optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Company name" {...field} data-testid="input-employer" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={financialForm.control}
                      name="occupation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Occupation (optional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Job title" {...field} data-testid="input-occupation" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={financialForm.control}
                    name="annualIncome"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Annual Income</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-income">
                              <SelectValue placeholder="Select range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="under_25k">Under $25,000</SelectItem>
                            <SelectItem value="25k_50k">$25,000 - $50,000</SelectItem>
                            <SelectItem value="50k_100k">$50,000 - $100,000</SelectItem>
                            <SelectItem value="100k_200k">$100,000 - $200,000</SelectItem>
                            <SelectItem value="200k_500k">$200,000 - $500,000</SelectItem>
                            <SelectItem value="over_500k">Over $500,000</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={financialForm.control}
                      name="netWorth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Total Net Worth</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-net-worth">
                                <SelectValue placeholder="Select range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="under_50k">Under $50,000</SelectItem>
                              <SelectItem value="50k_100k">$50,000 - $100,000</SelectItem>
                              <SelectItem value="100k_250k">$100,000 - $250,000</SelectItem>
                              <SelectItem value="250k_500k">$250,000 - $500,000</SelectItem>
                              <SelectItem value="500k_1m">$500,000 - $1,000,000</SelectItem>
                              <SelectItem value="over_1m">Over $1,000,000</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={financialForm.control}
                      name="liquidNetWorth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Liquid Net Worth</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-liquid-worth">
                                <SelectValue placeholder="Select range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="under_25k">Under $25,000</SelectItem>
                              <SelectItem value="25k_50k">$25,000 - $50,000</SelectItem>
                              <SelectItem value="50k_100k">$50,000 - $100,000</SelectItem>
                              <SelectItem value="100k_250k">$100,000 - $250,000</SelectItem>
                              <SelectItem value="250k_500k">$250,000 - $500,000</SelectItem>
                              <SelectItem value="over_500k">Over $500,000</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={financialForm.control}
                    name="fundingSource"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Source of Funds</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-funding-source">
                              <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="employment">Employment Income</SelectItem>
                            <SelectItem value="savings">Savings/Investments</SelectItem>
                            <SelectItem value="inheritance">Inheritance</SelectItem>
                            <SelectItem value="business">Business Income</SelectItem>
                            <SelectItem value="retirement">Retirement/Pension</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
                <CardFooter className="flex justify-between gap-2">
                  <Button type="button" variant="outline" onClick={goToPrevStep} data-testid="button-prev">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button type="submit" data-testid="button-next">
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        )}

        {currentStep === "trading-experience" && (
          <Card>
            <CardHeader>
              <CardTitle>Trading Experience</CardTitle>
              <CardDescription>Tell us about your investment experience and goals</CardDescription>
            </CardHeader>
            <Form {...tradingForm}>
              <form onSubmit={tradingForm.handleSubmit(handleTradingExperienceSubmit)}>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Experience Level</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={tradingForm.control}
                        name="stocksExperience"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Stocks & ETFs</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-stocks-exp">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="limited">Limited (1-2 years)</SelectItem>
                                <SelectItem value="moderate">Moderate (3-5 years)</SelectItem>
                                <SelectItem value="extensive">Extensive (5+ years)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={tradingForm.control}
                        name="optionsExperience"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Options</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-options-exp">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="limited">Limited (1-2 years)</SelectItem>
                                <SelectItem value="moderate">Moderate (3-5 years)</SelectItem>
                                <SelectItem value="extensive">Extensive (5+ years)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={tradingForm.control}
                        name="cryptoExperience"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Cryptocurrency</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-crypto-exp">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="limited">Limited (1-2 years)</SelectItem>
                                <SelectItem value="moderate">Moderate (3-5 years)</SelectItem>
                                <SelectItem value="extensive">Extensive (5+ years)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Investment Goals</h4>
                    <FormField
                      control={tradingForm.control}
                      name="investmentObjective"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Investment Objective</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-objective">
                                <SelectValue placeholder="Select objective" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="preservation">Capital Preservation</SelectItem>
                              <SelectItem value="income">Income Generation</SelectItem>
                              <SelectItem value="growth">Growth</SelectItem>
                              <SelectItem value="speculation">Speculation</SelectItem>
                              <SelectItem value="trading">Active Trading</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={tradingForm.control}
                        name="riskTolerance"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Risk Tolerance</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-risk">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="conservative">Conservative</SelectItem>
                                <SelectItem value="moderate">Moderate</SelectItem>
                                <SelectItem value="aggressive">Aggressive</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={tradingForm.control}
                        name="timeHorizon"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Investment Time Horizon</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-horizon">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="short">Short-term (1-3 years)</SelectItem>
                                <SelectItem value="medium">Medium-term (3-10 years)</SelectItem>
                                <SelectItem value="long">Long-term (10+ years)</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Trading Permissions</h4>
                    <div className="space-y-3">
                      <FormField
                        control={tradingForm.control}
                        name="enableOptionsTrading"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-3 space-y-0 p-4 border border-border rounded-lg">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-options" />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="text-foreground">Enable Options Trading</FormLabel>
                              <FormDescription>Trade calls, puts, and spreads. Requires approval.</FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={tradingForm.control}
                        name="enableCryptoTrading"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-3 space-y-0 p-4 border border-border rounded-lg">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-crypto" />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="text-foreground">Enable Cryptocurrency Trading</FormLabel>
                              <FormDescription>Trade Bitcoin, Ethereum, and other digital assets.</FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={tradingForm.control}
                        name="enableMarginTrading"
                        render={({ field }) => (
                          <FormItem className="flex items-start space-x-3 space-y-0 p-4 border border-border rounded-lg">
                            <FormControl>
                              <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-margin" />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="text-foreground">Enable Margin Trading</FormLabel>
                              <FormDescription>Borrow funds to increase buying power. Higher risk.</FormDescription>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between gap-2">
                  <Button type="button" variant="outline" onClick={goToPrevStep} data-testid="button-prev">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button type="submit" data-testid="button-next">
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        )}

        {currentStep === "documents" && (
          <Card>
            <CardHeader>
              <CardTitle>Documents & Agreements</CardTitle>
              <CardDescription>Verify your identity and accept required agreements</CardDescription>
            </CardHeader>
            <Form {...documentsForm}>
              <form onSubmit={documentsForm.handleSubmit(handleDocumentsSubmit)}>
                <CardContent className="space-y-6">
                  <FormField
                    control={documentsForm.control}
                    name="governmentIdType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Government-Issued ID Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-id-type">
                              <SelectValue placeholder="Select ID type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="drivers_license">Driver's License</SelectItem>
                            <SelectItem value="passport">Passport</SelectItem>
                            <SelectItem value="state_id">State ID Card</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>You'll be asked to upload a photo of your ID after approval</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-4">
                    <h4 className="font-medium text-foreground">Required Agreements</h4>
                    <FormField
                      control={documentsForm.control}
                      name="acceptedDisclosures"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0 p-4 border border-border rounded-lg">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-disclosures" />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-foreground">Risk Disclosures</FormLabel>
                            <FormDescription>I acknowledge that investing involves risk, including potential loss of principal.</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentsForm.control}
                      name="acceptedCustomerAgreement"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0 p-4 border border-border rounded-lg">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-customer-agreement" />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-foreground">Customer Agreement</FormLabel>
                            <FormDescription>I have read and agree to the Customer Agreement and Terms of Service.</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={documentsForm.control}
                      name="acceptedPrivacyPolicy"
                      render={({ field }) => (
                        <FormItem className="flex items-start space-x-3 space-y-0 p-4 border border-border rounded-lg">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} data-testid="checkbox-privacy" />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-foreground">Privacy Policy</FormLabel>
                            <FormDescription>I have read and agree to the Privacy Policy.</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between gap-2">
                  <Button type="button" variant="outline" onClick={goToPrevStep} data-testid="button-prev">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button type="submit" data-testid="button-next">
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardFooter>
              </form>
            </Form>
          </Card>
        )}

        {currentStep === "funding" && (
          <Card>
            <CardHeader>
              <CardTitle>Fund Your Account</CardTitle>
              <CardDescription>Choose how you'd like to fund your new account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setFormData({ ...formData, fundingMethod: "ach" })}
                  className={`p-4 border rounded-lg text-left transition-colors hover-elevate ${
                    formData.fundingMethod === "ach" ? "border-primary bg-primary/10" : "border-border"
                  }`}
                  data-testid="button-funding-ach"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Bank Transfer (ACH)</p>
                      <p className="text-sm text-muted-foreground">Link your bank account (3-5 days)</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, fundingMethod: "wire" })}
                  className={`p-4 border rounded-lg text-left transition-colors hover-elevate ${
                    formData.fundingMethod === "wire" ? "border-primary bg-primary/10" : "border-border"
                  }`}
                  data-testid="button-funding-wire"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <CreditCard className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Wire Transfer</p>
                      <p className="text-sm text-muted-foreground">Same-day transfer (fees may apply)</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, fundingMethod: "transfer" })}
                  className={`p-4 border rounded-lg text-left transition-colors hover-elevate ${
                    formData.fundingMethod === "transfer" ? "border-primary bg-primary/10" : "border-border"
                  }`}
                  data-testid="button-funding-transfer"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <TrendingUp className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">ACATS Transfer</p>
                      <p className="text-sm text-muted-foreground">Transfer from another brokerage (5-7 days)</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, fundingMethod: "later" })}
                  className={`p-4 border rounded-lg text-left transition-colors hover-elevate ${
                    formData.fundingMethod === "later" ? "border-primary bg-primary/10" : "border-border"
                  }`}
                  data-testid="button-funding-later"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted">
                      <AlertCircle className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Fund Later</p>
                      <p className="text-sm text-muted-foreground">Complete account setup first</p>
                    </div>
                  </div>
                </button>
              </div>

              {formData.fundingMethod && formData.fundingMethod !== "later" && (
                <div className="space-y-4">
                  <Label htmlFor="initialDeposit">Initial Deposit Amount</Label>
                  <Input
                    id="initialDeposit"
                    type="number"
                    placeholder="$0.00"
                    value={formData.initialDeposit}
                    onChange={(e) => setFormData({ ...formData, initialDeposit: e.target.value })}
                    data-testid="input-deposit"
                  />
                  <p className="text-sm text-muted-foreground">Minimum deposit: $0 (some features may require a minimum balance)</p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between gap-2">
              <Button type="button" variant="outline" onClick={goToPrevStep} data-testid="button-prev">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button onClick={handleFundingSubmit} disabled={!formData.fundingMethod} data-testid="button-next">
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>
        )}

        {currentStep === "review" && (
          <Card>
            <CardHeader>
              <CardTitle>Review Your Application</CardTitle>
              <CardDescription>Please review your information before submitting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">Account Type</h4>
                  <p className="text-muted-foreground capitalize">{formData.accountType.replace(/_/g, " ")}</p>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">Personal Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p className="text-muted-foreground">Name:</p>
                    <p className="text-foreground">{formData.firstName} {formData.lastName}</p>
                    <p className="text-muted-foreground">Date of Birth:</p>
                    <p className="text-foreground">{formData.dateOfBirth}</p>
                    <p className="text-muted-foreground">Phone:</p>
                    <p className="text-foreground">{formData.phone}</p>
                    <p className="text-muted-foreground">Address:</p>
                    <p className="text-foreground">{formData.address}, {formData.city}, {formData.state} {formData.zipCode}</p>
                  </div>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">Trading Permissions</h4>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">Stocks & ETFs</span>
                    {formData.enableOptionsTrading && (
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">Options</span>
                    )}
                    {formData.enableCryptoTrading && (
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">Cryptocurrency</span>
                    )}
                    {formData.enableMarginTrading && (
                      <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">Margin</span>
                    )}
                  </div>
                </div>
                <div className="p-4 border border-border rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">Funding Method</h4>
                  <p className="text-muted-foreground capitalize">{formData.fundingMethod?.replace(/_/g, " ") || "To be determined"}</p>
                </div>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">
                      By submitting this application, you certify that all information provided is accurate and complete.
                      Your application will be reviewed within 1-2 business days.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between gap-2">
              <Button type="button" variant="outline" onClick={goToPrevStep} data-testid="button-prev">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleFinalSubmit}
                disabled={submitApplicationMutation.isPending}
                data-testid="button-submit"
              >
                {submitApplicationMutation.isPending ? (
                  <>
                    <div className="h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Submit Application
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
}
